'use client';

import React, { FC, useEffect } from 'react';
import { InventoryStatusCode } from '@/lib/shopify';
import { Button } from '@/components';
import useProductContext from '@/context/ProductContext';
import { TypeProduct } from '@/core/generated-types';
import { useProductValidations } from '@/hooks/useProductValidations';
import { CTA_COLORS } from '../types';
import { getCTAStyles } from '@/components/utils';

interface AddButtonProps {
  inventoryStatus: InventoryStatusCode | null;
  onClick?: () => void;
  isSelected?: boolean;
  isLoading?: boolean;
  product?: TypeProduct | null;
  isDisabledFromCaller?: boolean;
  dataTestId?: string;
}

const inventoryStatusFallbacks = {
  outOfStock: 'Out of Stock',
  availableCta: 'Add to Cart',
  outOfStockCta: 'Out of Stock',
  preSaleCta: 'Pre-Order Now',
  addedToOrder: 'Added to Order',
};

const AddButton: FC<AddButtonProps> = ({
  inventoryStatus,
  onClick,
  isSelected = false,
  product,
  isLoading = false,
  isDisabledFromCaller = false,
  dataTestId,
}) => {
  const { inventoryStatusCopy } = useProductContext();

  const isValidProduct = useProductValidations(product);
  let isDisabled = !isValidProduct;

  const ctaColor = CTA_COLORS.DEFAULT;

  let buttonText = '';
  let buttonClasses = '';

  // This logic ensures submit buttons outside ths scope of the add to cart form align with the cta text assigned here.
  useEffect(() => {
    const submitButtons = document.querySelectorAll(
      'button[type="submit"][form="add-to-cart-form"]',
    );

    submitButtons.forEach((button) => {
      button.textContent = buttonText;
    });
  }, [buttonText]);

  const addToCartText =
    product?.fields?.addToCartText ??
    (inventoryStatusCopy?.availableCta ||
      inventoryStatusFallbacks.availableCta);

  const addedToOrderText =
    inventoryStatusCopy?.addedToOrder || inventoryStatusFallbacks.addedToOrder;

  switch (inventoryStatus) {
    default: {
      buttonText = isSelected ? addedToOrderText : addToCartText;
      buttonClasses = getCTAStyles(ctaColor);
      break;
    }
    case InventoryStatusCode.OUT_OF_STOCK: {
      buttonText =
        inventoryStatusCopy?.outOfStockCta ||
        inventoryStatusFallbacks.outOfStockCta;
      isDisabled = true;
      break;
    }
    case InventoryStatusCode.PRESALE: {
      buttonText =
        inventoryStatusCopy?.preSaleCta || inventoryStatusFallbacks.preSaleCta;
      break;
    }
  }

  if (isDisabled) {
    buttonClasses = 'bg-grey-midtone border-grey-midtone';
  }

  return (
    <Button
      type="submit"
      ctaText={buttonText}
      fullWidth={true}
      classes={`btn-responsive ${buttonClasses}`}
      disabled={isDisabled || isDisabledFromCaller || isSelected || isLoading}
      includeSpinner={isLoading}
      onClick={onClick}
      testId={dataTestId}
    />
  );
};

export default AddButton;
